import { CloseOutlined } from '@ant-design/icons'
import { createContext, useContext, useState } from 'react'
import styled, { css } from 'styled-components'
import { ReactComponent as FullScreenExpand } from '../../assets/FullScreenExpand.svg'

type fullScreenContainerProps = {
  children: JSX.Element
  title: string | JSX.Element
  titleUnderline?: boolean
  fullScreenOnlyTitleElements?: JSX.Element
}

type FullScreenContainerContextType = {
  setFullScreenTitle: React.Dispatch<React.SetStateAction<string | JSX.Element>>
}

const FullScreenContainerContext = createContext<
  FullScreenContainerContextType | undefined
>(undefined)

export const useFullScreenContainerContext =
  (): FullScreenContainerContextType => {
    const context = useContext(FullScreenContainerContext)
    return context!
  }

const FullScreenContainerDiv = styled.div<{ state: 'fullScreen' | 'normal' }>`
  ${(props) => {
    if (props.state === 'fullScreen')
      return css`
        position: fixed;
        width: 100vw;
        height: 100%;
        min-height: 100vh;
        left: 0px;
        top: 0px;
        display: flex;
        flex-direction: column;
        align-items: center;
        background: white;
        z-index: 10;
        overflow: auto;
        padding: 24px;

        & > .fullScreenOnlyElements {
          padding: 10px;
          margin-right: auto;
        }
        & > .fullScreenHeader {
          justify-content: space-between;
        }

        &
          > .fullScreenHeader
          > .fullScreenControl
          > button.fullScreenControlButton {
          & svg {
            width: 20px;
            height: 20px;
            fill: white;
          }
          background-color: grey !important;
          border-radius: 100%;
          padding: 10px;
        }

        & .fullScreenContent {
          width: 100%;
          height: 100%;
        }
      `
    else
      return css`
        position: relative;
        padding: 0px 0px 0px;
        height: 100%;
        & > .fullScreenHeader {
          justify-content: flex-start;
        }
        &
          > .fullScreenHeader
          > .fullScreenControl
          > button.fullScreenControlButton {
          & svg {
            width: 25px;
            height: 25px;
            fill: black;
          }
          padding: 5px;
        }
      `
  }}
  &>.fullScreenHeader>.fullScreenControl>button.fullScreenControlButton {
    background: transparent;
    outline: 0px;
    margin: 0px;
    border: none;
    display: flex;
  }
  & .fullScreenContent {
    width: 100%;
    height: calc(100% - 50px);
  }
  & > .fullScreenHeader {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    width: 100%;
    height: max-content;
    padding: 10px;
    font-size: 1.25rem;
    font-weight: 600;
    & .title.underline {
      text-decoration: underline;
      padding-right: 10px;
    }
    & .title {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 10px;
    }
    & {
      justify-content: space-between;
    }
  }
`

const FullScreenContainer = ({
  children,
  title,
  titleUnderline = true,
  fullScreenOnlyTitleElements,
}: fullScreenContainerProps) => {
  const [fullScreenState, setFullScreenState] = useState(false)
  const [fullScreenTitle, setFullScreenTitle] = useState(title)

  return (
    <FullScreenContainerDiv
      className={fullScreenState ? 'fullScreen' : 'normal'}
      state={fullScreenState ? 'fullScreen' : 'normal'}
    >
      <FullScreenContainerContext.Provider
        value={{
          setFullScreenTitle: setFullScreenTitle,
        }}
      >
        <div className='fullScreenOnlyElements'>
          {fullScreenState && fullScreenOnlyTitleElements}
        </div>
        <div className='fullScreenHeader'>
          <div className={`title ${titleUnderline ? 'underline' : ''}`}>
            {fullScreenTitle}
          </div>
          <div
            className='fullScreenControl'
            onClick={() => setFullScreenState((prev) => !prev)}
          >
            <button className='fullScreenControlButton'>
              {fullScreenState ? <CloseOutlined /> : <FullScreenExpand />}
            </button>
          </div>
        </div>
        <div className='fullScreenContent'>{children}</div>
      </FullScreenContainerContext.Provider>
    </FullScreenContainerDiv>
  )
}

export default FullScreenContainer
